import PropTypes from "prop-types";
import { useTheme } from "@mui/material/styles";
import {
  Box,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableFooter,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import { DURATION_INTERVALS } from "modules/came/model/permanenceModel";
import { formatReportDate } from "modules/came/model/frequencyModel";
const number = (value) => Number(value || 0).toLocaleString("es-CO");
const intervalDescription = (index) =>
  index === 0
    ? "Desde 0 hasta 15 minutos"
    : index === DURATION_INTERVALS.length - 1
    ? "Más de 9 horas"
    : `Más de ${DURATION_INTERVALS[index - 1].max} y hasta ${
        DURATION_INTERVALS[index].max
      } minutos`;
export default function PermanenciaTable({ data = null, loading = false }) {
  const dark = useTheme().palette.mode === "dark";
  if (loading)
    return (
      <Box sx={{ p: 5, textAlign: "center" }}>
        <CircularProgress size={28} />
        <Typography variant="body2" sx={{ mt: 1 }}>
          Consultando permanencia…
        </Typography>
      </Box>
    );
  return (
    <Box sx={{ pt: 3 }}>
      <Box sx={{ px: { xs: 2, sm: 3 }, pb: 2 }}>
        <Typography variant="subtitle2" fontWeight={600}>
          Permanencia por día
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Visitas terminadas, agrupadas por fecha de salida · Hora de Colombia
        </Typography>
      </Box>
      {data && !data.totalVisits && (
        <Typography variant="body2" sx={{ px: 3, pb: 2 }} color="text.secondary">
          No hay visitas terminadas para los filtros seleccionados.
        </Typography>
      )}
      <TableContainer
        tabIndex={0}
        role="region"
        aria-label="Tabla de permanencia por duración"
        sx={{ overflow: "auto", maxHeight: "min(65vh, 640px)", scrollbarWidth: "thin" }}
      >
        <Table
          stickyHeader
          size="small"
          aria-label="Visitas por fecha de salida e intervalo de permanencia"
          sx={{
            minWidth: 1180,
            borderCollapse: "separate",
            "& th, & td": {
              textAlign: "center",
              fontSize: "0.75rem",
              px: 1.5,
              py: 1.5,
              whiteSpace: "nowrap",
              borderRight: "1px solid",
              borderRightColor: "divider",
            },
            "& thead": { display: "table-header-group", p: 0 },
            "& thead th": { bgcolor: "#16364b", color: "#fff", fontWeight: 700 },
            "& tbody tr:nth-of-type(even)": { bgcolor: "action.hover" },
            "& tbody tr:hover td": { bgcolor: "action.selected" },
            "& tfoot th, & tfoot td": {
              position: "sticky",
              bottom: 0,
              bgcolor: dark ? "#193e38" : "#dceee6",
              color: dark ? "#9be3ba" : "#20533c",
              fontWeight: 700,
              zIndex: 2,
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell sx={{ left: 0, zIndex: "4 !important", minWidth: 120 }}>
                Fecha de salida
              </TableCell>
              {DURATION_INTERVALS.map((interval, index) => (
                <TableCell key={interval.key}>
                  <Tooltip title={intervalDescription(index)} arrow>
                    <Box component="span">{interval.label}</Box>
                  </Tooltip>
                </TableCell>
              ))}
              <TableCell>Total general</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!data && (
              <TableRow>
                <TableCell colSpan={14}>
                  El informe estará disponible al conectar el servicio de visitas.
                </TableCell>
              </TableRow>
            )}
            {(data?.rows || []).map((row) => (
              <TableRow key={row.fecha}>
                <TableCell
                  component="th"
                  scope="row"
                  sx={{
                    position: "sticky",
                    left: 0,
                    zIndex: 1,
                    bgcolor: "background.paper",
                    fontWeight: 600,
                  }}
                >
                  {formatReportDate(row.fecha)}
                </TableCell>
                {DURATION_INTERVALS.map((interval) => (
                  <TableCell
                    key={interval.key}
                    sx={{ color: row[interval.key] ? "text.primary" : "text.disabled" }}
                  >
                    {number(row[interval.key])}
                  </TableCell>
                ))}
                <TableCell sx={{ fontWeight: 700, bgcolor: "action.hover" }}>
                  {number(row.total)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
          {data && (
            <TableFooter>
              <TableRow>
                <TableCell component="th" scope="row" sx={{ left: 0, zIndex: "3 !important" }}>
                  Total general
                </TableCell>
                {DURATION_INTERVALS.map((interval) => (
                  <TableCell key={interval.key}>{number(data.totals[interval.key])}</TableCell>
                ))}
                <TableCell>{number(data.totalVisits)}</TableCell>
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </TableContainer>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", px: 3, py: 2 }}>
        Cada visita se cuenta en un solo intervalo. Los límites superiores están incluidos: 15 min
        en 0–15; 30 min en 16–30; 60 min en 1 hora.
      </Typography>
    </Box>
  );
}
PermanenciaTable.propTypes = {
  data: PropTypes.shape({
    rows: PropTypes.array.isRequired,
    totals: PropTypes.object.isRequired,
    totalVisits: PropTypes.number.isRequired,
  }),
  loading: PropTypes.bool,
};
